/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */


import {NgModule, Directive, Inject, Optional, ElementRef, InjectionToken} from '@angular/core';

export const /** @type {?} */ MATERIAL_COMPATIBILITY_MODE = new InjectionToken<boolean>('md-compatibility-mode');
/**
 * Returns an exception to be thrown if the consumer has used
 * an invalid Material prefix on a component.
 * \@docs-private
 * @param {?} prefix
 * @param {?} nodeName
 * @return {?}
 */
export function getMdCompatibilityInvalidPrefixError(prefix: string, nodeName: string) {
  return Error(`The "${prefix}-" prefix cannot be used in ng-material v1 compatibility mode. ` +
                   `It was used on an "${nodeName.toLowerCase()}" element.`);
}
/**
 * Selector that matches all elements that may have style collisions with AngularJS Material.
 */
export const MAT_ELEMENTS_SELECTOR = `
  [mat-button],
  [mat-fab],
  [mat-icon-button],
  [mat-mini-fab],
  [mat-raised-button],
  [matCardSubtitle],
  [matCardTitle],
  [matCellDef],
  [matColumnDef],
  [matDialogActions],
  [matDialogClose],
  [matDialogContent],
  [matDialogTitle],
  [matHeaderCellDef],
  [matHeaderRowDef],
  [matLine],
  [matRowDef],
  [matStepLabel],
  [matStepperNext],
  [matStepperPrevious],
  [matTabLabel],
  [matTabLink],
  [matTabNav],
  [matTooltip],
  [matInput],
  [matPrefix],
  [matSuffix],
  mat-autocomplete,
  mat-button-toggle,
  mat-button-toggle,
  mat-button-toggle-group,
  mat-card,
  mat-card-actions,
  mat-card-content,
  mat-card-footer,
  mat-card-header,
  mat-card-subtitle,
  mat-card-title,
  mat-card-title-group,
  mat-cell,
  mat-checkbox,
  mat-chip,
  mat-dialog-actions,
  mat-dialog-container,
  mat-dialog-content,
  mat-divider,
  mat-error,
  mat-grid-list,
  mat-grid-tile,
  mat-grid-tile-footer,
  mat-grid-tile-header,
  mat-header-cell,
  mat-header-row,
  mat-hint,
  mat-horizontal-stepper,
  mat-icon,
  mat-input-container,
  mat-form-field,
  mat-list,
  mat-list-item,
  mat-menu,
  mat-nav-list,
  mat-option,
  mat-placeholder,
  mat-progress-bar,
  mat-pseudo-checkbox,
  mat-radio-button,
  mat-radio-group,
  mat-row,
  mat-select,
  mat-sidenav,
  mat-sidenav-container,
  mat-slider,
  mat-spinner,
  mat-step,
  mat-tab,
  mat-table,
  mat-tab-group,
  mat-toolbar,
  mat-vertical-stepper`;
/**
 * Selector that matches all elements that may have style collisions with AngularJS Material.
 */
export const MD_ELEMENTS_SELECTOR = `
  [md-button],
  [md-fab],
  [md-icon-button],
  [md-mini-fab],
  [md-raised-button],
  [mdCardSubtitle],
  [mdCardTitle],
  [mdCellDef],
  [mdColumnDef],
  [mdDialogActions],
  [mdDialogClose],
  [mdDialogContent],
  [mdDialogTitle],
  [mdHeaderCellDef],
  [mdHeaderRowDef],
  [mdLine],
  [mdRowDef],
  [mdStepLabel],
  [mdStepperNext],
  [mdStepperPrevious],
  [mdTabLabel],
  [mdTabLink],
  [mdTabNav],
  [mdTooltip],
  [mdInput],
  [mdPrefix],
  [mdSuffix],
  md-autocomplete,
  md-button-toggle,
  md-button-toggle,
  md-button-toggle-group,
  md-card,
  md-card-actions,
  md-card-content,
  md-card-footer,
  md-card-header,
  md-card-subtitle,
  md-card-title,
  md-card-title-group,
  md-cell,
  md-checkbox,
  md-chip,
  md-dialog-actions,
  md-dialog-container,
  md-dialog-content,
  md-divider,
  md-error,
  md-grid-list,
  md-grid-tile,
  md-grid-tile-footer,
  md-grid-tile-header,
  md-header-cell,
  md-header-row,
  md-hint,
  md-horizontal-stepper,
  md-icon,
  md-input-container,
  md-form-field,
  md-list,
  md-list-item,
  md-menu,
  md-nav-list,
  md-option,
  md-placeholder,
  md-progress-bar,
  md-pseudo-checkbox,
  md-radio-button,
  md-radio-group,
  md-row,
  md-select,
  md-sidenav,
  md-sidenav-container,
  md-slider,
  md-spinner,
  md-step,
  md-tab,
  md-table,
  md-tab-group,
  md-toolbar,
  md-vertical-stepper`;
/**
 * Directive that enforces that the `mat-` prefix cannot be used.
 */
export class MatPrefixRejector {
/**
 * @param {?} isCompatibilityMode
 * @param {?} elementRef
 */
constructor(
      isCompatibilityMode: boolean,
    elementRef: ElementRef) {

    if (!isCompatibilityMode) {
      throw getMdCompatibilityInvalidPrefixError('mat', elementRef.nativeElement.nodeName);
    }
  }
static decorators: DecoratorInvocation[] = [
{ type: Directive, args: [{selector: MAT_ELEMENTS_SELECTOR}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: undefined, decorators: [{ type: Optional }, { type: Inject, args: [MATERIAL_COMPATIBILITY_MODE, ] }, ]},
{type: ElementRef, },
];
}

function MatPrefixRejector_tsickle_Closure_declarations() {
/** @type {?} */
MatPrefixRejector.decorators;
/**
 * @nocollapse
 * @type {?}
 */
MatPrefixRejector.ctorParameters;
}

/**
 * Directive that enforces that the `md-` prefix cannot be used.
 */
export class MdPrefixRejector {
/**
 * @param {?} isCompatibilityMode
 * @param {?} elementRef
 */
constructor(
      isCompatibilityMode: boolean,
    elementRef: ElementRef) {

    if (isCompatibilityMode) {
      throw getMdCompatibilityInvalidPrefixError('md', elementRef.nativeElement.nodeName);
    }
  }
static decorators: DecoratorInvocation[] = [
{ type: Directive, args: [{selector: MD_ELEMENTS_SELECTOR}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: undefined, decorators: [{ type: Optional }, { type: Inject, args: [MATERIAL_COMPATIBILITY_MODE, ] }, ]},
{type: ElementRef, },
];
}

function MdPrefixRejector_tsickle_Closure_declarations() {
/** @type {?} */
MdPrefixRejector.decorators;
/**
 * @nocollapse
 * @type {?}
 */
MdPrefixRejector.ctorParameters;
}

/**
 * Module that enforces the default compatibility mode settings. When this module is loaded
 * without NoConflictStyleCompatibilityMode also being imported, it will throw an error if
 * there are any uses of the `mat-` prefix.
 */
export class CompatibilityModule {static decorators: DecoratorInvocation[] = [
{ type: NgModule, args: [{
  declarations: [MatPrefixRejector, MdPrefixRejector],
  exports: [MatPrefixRejector, MdPrefixRejector],
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
];
}

function CompatibilityModule_tsickle_Closure_declarations() {
/** @type {?} */
CompatibilityModule.decorators;
/**
 * @nocollapse
 * @type {?}
 */
CompatibilityModule.ctorParameters;
}

/**
 * Module that enforces "no-conflict" compatibility mode settings. When this module is loaded,
 * it will throw an error if there are any uses of the `md-` prefix.
 */
export class NoConflictStyleCompatibilityMode {static decorators: DecoratorInvocation[] = [
{ type: NgModule, args: [{
  providers: [{
    provide: MATERIAL_COMPATIBILITY_MODE, useValue: true,
  }],
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
];
}

function NoConflictStyleCompatibilityMode_tsickle_Closure_declarations() {
/** @type {?} */
NoConflictStyleCompatibilityMode.decorators;
/**
 * @nocollapse
 * @type {?}
 */
NoConflictStyleCompatibilityMode.ctorParameters;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
